import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { LoaderSymbol } from '../../components/loading';
import { useUnsavedGuard, guardedExit } from '../../hooks/useUnsavedGuard';
import { designerFrameSrc, designerOrigin, type DesignerMode } from './reportDesignerUrl';

export type { DesignerMode } from './reportDesignerUrl';

// ─────────────────────────────────────────────────────────────────────────────
// ReportDesignerFrame — the Custom Report Builder's DESIGN / PREVIEW view.
//
// DESIGN — the DevExpress v20.1 Web Report Designer served by
// src/AlpacaIntelligence.ReportDesigner (a separate host the API forwards
// /reports/* to), embedded in an <iframe>:
//   Design → {api}/reports/designer?report={name}&rds={session}
// `rds` is a designer-session capability token minted by
// POST /api/v1/custom-report-templates/designer-session (an iframe cannot send the
// SPA's JWT — ADR-0014); the forwarder turns it into a cookie for the designer's own
// follow-up requests, so it is sent exactly once, on the first load.
//
// PREVIEW — renders the template's SAVED DESIGNED LAYOUT to PDF for a chosen job
// via GET /api/v1/custom-report-templates/{id}/layout-preview-pdf, using the SAME
// DevExpress layout renderer as Custom Reporting.  So Preview shows the real report
// output (banded layout, real data), NOT the designer host's own script-running
// viewer — which needs job context the host doesn't have and would error.  A small
// job picker seeds from GET .../preview-jobs (recent JobIDs).
//
// Unsaved-changes guard (standing rule 15): the design iframe publishes its dirty
// state over postMessage ({type:'alpaca-rd:state', dirty}); Back runs the shared
// "Unsaved changes will be lost" confirm through guardedExit, and the state is
// registered with useUnsavedGuard so sidebar navigation / Sign Out ask too.
// ─────────────────────────────────────────────────────────────────────────────
interface Props {
  /** Template name (colCustomReportDesignerName); null / '' = a NEW blank report. */
  reportName: string | null;
  /** Row id (coluqCustomReportDesignerID) — needed for the PDF preview; null for New. */
  templateId: number | null;
  mode: DesignerMode;
  onModeChange: (mode: DesignerMode) => void;
  /** Return to the list (the page reloads the grid — a save may have added a row). */
  onBack: () => void;
  /** The designer's Prev/Next pill asks the host to switch to another report by name, so the
   *  host header + Preview (templateId) + unsaved guard stay in sync (see Designer.cshtml). */
  onNavigate?: (reportName: string) => void;
}

/** The catalog name of the MDL Study Report template (Custom Report Builder Phase 3). Its preview
 *  is keyed by MDL study, not JobID, so the designer shows a study picker + a dedicated render. */
const MDL_REPORT_NAME = 'MDL Study Report';
/** One MDL-study option for the preview study picker: key = study Oid, label = "MDL-id - Test". */
interface MdlStudyOption { key: string; label: string; }

interface DesignerSession { token: string; idleSeconds: number; }

/** Messages the designer page posts to its host (see Designer.cshtml host bridge). */
interface DesignerStateMessage { type: 'alpaca-rd:state'; dirty: boolean; report?: string; }
interface DesignerNavigateMessage { type: 'alpaca-rd:navigate'; report: string; }

function apiErrMsg(e: unknown, fallback: string): string {
  return (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message
    ?? (e instanceof Error ? e.message : fallback);
}

export function ReportDesignerFrame({ reportName, templateId, mode, onModeChange, onBack, onNavigate }: Props) {
  const notify = useNotify();
  const [session, setSession] = useState<DesignerSession | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  // Kept in a ref so the postMessage listener (subscribed once) always calls the latest handler
  // without re-subscribing on every render.
  const onNavigateRef = useRef(onNavigate);
  useEffect(() => { onNavigateRef.current = onNavigate; }, [onNavigate]);

  // ── Designer session (design mode only — the DevExpress iframe needs it) ────
  useEffect(() => {
    if (mode !== 'design') return;
    let cancelled = false;
    setSession(null); setSessionError(null);
    (async () => {
      try {
        const res = await api.post('/api/v1/custom-report-templates/designer-session');
        if (!cancelled) setSession((res.data as { data: DesignerSession }).data);
      } catch (e) {
        const msg = apiErrMsg(e, 'API error.');
        if (!cancelled) { setSessionError(msg); notify.error(`Could not open the report designer: ${msg}`); }
      }
    })();
    return () => { cancelled = true; };
  }, [mode, notify]);

  // ── Dirty state from the design iframe (postMessage bridge) ────────────────
  useEffect(() => {
    const origin = designerOrigin();
    const onMessage = (ev: MessageEvent) => {
      if (ev.origin !== origin) return;
      const data = ev.data as Partial<DesignerStateMessage> | Partial<DesignerNavigateMessage> | null;
      if (data?.type === 'alpaca-rd:state') setDirty(!!data.dirty);
      else if (data?.type === 'alpaca-rd:navigate' && data.report) onNavigateRef.current?.(data.report);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
  // Preview never edits; a reload of the design frame starts clean.
  useEffect(() => { setDirty(false); }, [mode, reportName]);
  useUnsavedGuard(dirty && mode === 'design');

  const designSrc = useMemo(
    () => (mode === 'design' && session ? designerFrameSrc('design', reportName, session.token) : null),
    [mode, session, reportName]);

  // ── PDF preview (mode === 'preview') ───────────────────────────────────────
  // The MDL Study Report is keyed by MDL study (not JobID), so it previews via its own study
  // picker + render endpoint (Custom Report Builder Phase 3).
  const isMdl = reportName === MDL_REPORT_NAME;
  const [jobs, setJobs] = useState<string[]>([]);
  const [studies, setStudies] = useState<MdlStudyOption[]>([]);
  const [job, setJob] = useState<string>('');   // selected value: JobID, or (MDL) study Oid
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const pdfUrlRef = useRef<string | null>(null);

  const revokePdf = useCallback(() => {
    if (pdfUrlRef.current) { URL.revokeObjectURL(pdfUrlRef.current); pdfUrlRef.current = null; }
  }, []);

  // Load the picker once, when entering preview — recent jobs, or (MDL) recent studies.
  useEffect(() => {
    if (mode !== 'preview') return;
    let cancelled = false;
    (async () => {
      try {
        if (isMdl) {
          const res = await api.get('/api/v1/custom-report-templates/mdl-studies', { params: { take: 100 } });
          const list = (res.data as { data: MdlStudyOption[] }).data ?? [];
          if (!cancelled) { setStudies(list); setJob(prev => prev || list[0]?.key || ''); }
        } else {
          const res = await api.get('/api/v1/custom-report-templates/preview-jobs', { params: { take: 25 } });
          const list = (res.data as { data: string[] }).data ?? [];
          if (!cancelled) { setJobs(list); setJob(prev => prev || list[0] || ''); }
        }
      } catch { /* the picker just stays empty — preview falls back to the newest job / study */ }
    })();
    return () => { cancelled = true; };
  }, [mode, isMdl]);

  // Render the PDF for the current template + selected job / study.
  const loadPdf = useCallback(async (selected: string) => {
    if (templateId == null) return;
    setPdfLoading(true); setPdfError(null);
    try {
      const endpoint = isMdl
        ? `/api/v1/custom-report-templates/${templateId}/mdl-preview-pdf`
        : `/api/v1/custom-report-templates/${templateId}/layout-preview-pdf`;
      const params = isMdl
        ? (selected ? { studyId: selected } : undefined)
        : (selected ? { jobId: selected } : undefined);
      const res = await api.get(endpoint, { params, responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      revokePdf();
      pdfUrlRef.current = url;
      setPdfUrl(url);
    } catch (e) {
      // A blob-typed error response has the JSON message inside the Blob.
      let msg = apiErrMsg(e, 'Could not render the preview.');
      const blob = (e as { response?: { data?: unknown } })?.response?.data;
      if (blob instanceof Blob) { try { msg = JSON.parse(await blob.text())?.error?.message ?? msg; } catch { /* keep msg */ } }
      setPdfError(msg);
      revokePdf(); setPdfUrl(null);
    } finally {
      setPdfLoading(false);
    }
  }, [templateId, revokePdf, isMdl]);

  // Load / reload the PDF when preview opens or the job changes.
  useEffect(() => {
    if (mode !== 'preview') return;
    void loadPdf(job);
  }, [mode, job, loadPdf]);

  // Free the object URL on unmount / when leaving preview.
  useEffect(() => () => revokePdf(), [revokePdf]);
  useEffect(() => { if (mode !== 'preview') { revokePdf(); setPdfUrl(null); } }, [mode, revokePdf]);

  const goBack = useCallback(() => { void guardedExit(onBack); }, [onBack]);
  const toDesign = useCallback(() => onModeChange('design'), [onModeChange]);
  const toPreview = useCallback(() => {
    if (!reportName) { notify.warning('Save the report first to preview it.'); return; }
    if (templateId == null) { notify.warning('Save the report first, then reopen it to preview.'); return; }
    onModeChange('preview');
  }, [reportName, templateId, onModeChange, notify]);

  // Bespoke action row → keys declared next to the buttons (standing rule 14b).
  useHotkeys([
    { combo: 'alt+p', label: mode === 'preview' ? 'Design' : 'Preview', group: 'Report Designer', order: 1,
      run: () => (mode === 'preview' ? toDesign() : toPreview()) },
    { combo: 'esc', label: 'Back to list', group: 'Report Designer', order: 2, run: goBack },
  ]);

  const title = reportName || 'New Report';

  return (
    <div className="rdf">
      <div className="rdf-bar">
        <div className="rdf-title">
          <i className="ti ti-layout-grid" />
          <span className="rdf-name">{title}</span>
          <span className="rdf-sub">{mode === 'preview' ? 'Preview' : 'Design'} · Report Designer</span>
          {dirty && mode === 'design' && <span className="rdf-dirty" title="Unsaved changes in the designer">● Unsaved</span>}
        </div>
        <div className="rdf-actions">
          {/* Preview picker — which job's (or MDL study's) data the layout renders against. */}
          {mode === 'preview' && isMdl && (
            <label className="rdf-jobpick" title="The MDL study whose data fills the preview">
              <span>Study</span>
              <select value={job} onChange={e => setJob(e.target.value)} disabled={pdfLoading || studies.length === 0}>
                {studies.length === 0 && <option value="">(newest)</option>}
                {studies.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </label>
          )}
          {mode === 'preview' && !isMdl && (
            <label className="rdf-jobpick" title="The job whose data fills the preview">
              <span>Job</span>
              <select value={job} onChange={e => setJob(e.target.value)} disabled={pdfLoading || jobs.length === 0}>
                {jobs.length === 0 && <option value="">(newest)</option>}
                {jobs.map(j => <option key={j} value={j}>{j}</option>)}
              </select>
            </label>
          )}
          <div className="rdf-seg" role="group" aria-label="Designer mode">
            <button type="button" className={`ca-btn${mode === 'design' ? ' ca-btn-solid' : ''}`}
                    onClick={toDesign} title="Design the report layout">
              <i className="ti ti-pencil" /> Design
            </button>
            <button type="button" className={`ca-btn${mode === 'preview' ? ' ca-btn-solid' : ''}`}
                    onClick={toPreview} disabled={!reportName}
                    title={reportName ? `Preview this report (${formatCombo('alt+p')})` : 'Save the report first to preview'}>
              <i className="ti ti-eye" /> Preview
            </button>
          </div>
          <button type="button" className="ca-btn" onClick={goBack} title={`Back to the template list (${formatCombo('esc')})`}>
            <i className="ti ti-arrow-left" /> Back
          </button>
        </div>
      </div>

      <div className="rdf-frame">
        {mode === 'preview' ? (
          pdfError ? (
            <div className="rdf-msg rdf-msg-err">
              <div>
                <i className="ti ti-alert-triangle" /> {pdfError}
                <div style={{ marginTop: 8 }}>
                  <button type="button" className="ca-btn" onClick={() => void loadPdf(job)}>
                    <i className="ti ti-refresh" /> Retry
                  </button>
                </div>
              </div>
            </div>
          ) : pdfLoading || !pdfUrl ? (
            <div className="rdf-msg"><LoaderSymbol size={28} /> Rendering the preview…</div>
          ) : (
            <iframe key={pdfUrl} title={`Report Preview — ${title}`} src={pdfUrl} className="rdf-iframe" />
          )
        ) : sessionError ? (
          <div className="rdf-msg rdf-msg-err"><i className="ti ti-alert-triangle" /> {sessionError}</div>
        ) : !designSrc ? (
          <div className="rdf-msg"><LoaderSymbol size={28} /> Opening the report designer…</div>
        ) : (
          <iframe key={designSrc} title={`Report Designer — ${title}`} src={designSrc} className="rdf-iframe" />
        )}
      </div>

      <style>{rdfStyles}</style>
    </div>
  );
}

const rdfStyles = `
  .rdf { display:flex; flex-direction:column; flex:1 1 auto; min-height:0; }
  .rdf-bar { display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap;
    padding:8px 14px; border-bottom:1px solid var(--ai-line); background:var(--ai-header-bg); }
  .rdf-title { display:flex; align-items:center; gap:8px; min-width:0; }
  .rdf-title i { color:var(--brand-accent, #e8670a); font-size:16px; }
  .rdf-name { font-size:13px; font-weight:800; color:var(--ai-ink); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:40vw; }
  .rdf-sub { font-size:12px; color:var(--ai-muted); white-space:nowrap; }
  .rdf-dirty { font-size:11px; font-weight:700; color:var(--brand-accent-hover, #c04a0a);
    background:color-mix(in srgb, var(--brand-accent, #e8670a) 10%, #fff);
    border:1px solid color-mix(in srgb, var(--brand-accent, #e8670a) 30%, #fff); border-radius:12px; padding:2px 8px; }
  .rdf-actions { display:flex; align-items:center; gap:8px; }
  .rdf-jobpick { display:inline-flex; align-items:center; gap:6px; font-size:12px; color:var(--ai-muted); }
  .rdf-jobpick select { font-size:12px; padding:4px 8px; border:1px solid var(--ai-line); border-radius:6px; background:#fff; color:var(--ai-ink); }
  .rdf-seg { display:inline-flex; gap:4px; }
  .rdf-frame { flex:1 1 auto; min-height:0; position:relative; background:#fff; }
  .rdf-iframe { position:absolute; inset:0; width:100%; height:100%; border:0; display:block; }
  .rdf-msg { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; gap:10px;
    color:var(--ai-muted); font-size:13px; padding:24px; text-align:center; }
  .rdf-msg-err { color:var(--ai-red); }
`;
